import { FormControl, Stack, Typography } from '@mui/material';
import { ConditionDatesForm } from '@retail/calculus/components';
import { useController, useFormContext } from 'react-hook-form';
import { PriceSuggestionPrice } from '@retail/price-suggestion/types';
import { PriceSuggestionType } from '@retail/products/types';
import { useCallback } from 'react';
import { usePriceSuggestionTFunction } from '@retail/price-suggestion/i18n';
import { HelperText } from '@shared/components';
import {
  FollowPriceSuggestionSchema,
  PriceSuggestionPrioritization,
} from '@retail/price-suggestion/components';
import { useFetchPriceSuggestionItemPrices } from '@retail/price-suggestion/data-access';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';

interface Props {
  mgSupplierId: number;
  mgItemNumber: number;
}

export function CreateItemPriceSuggestionConditionForm({ mgSupplierId, mgItemNumber }: Props) {
  const t = usePriceSuggestionTFunction();
  const { assortmentId } = useSelectedOrgUnit();

  const { control } = useFormContext<FollowPriceSuggestionSchema>();
  const priceSuggestionController = useController({ name: 'priceSuggestionPriority', control });

  const { data: itemLevelPriceSuggestionDTO = [] } = useFetchPriceSuggestionItemPrices(
    {
      assortmentId,
      mgSupplierId,
      mgItemNumber,
    },
    { suspense: false }
  );
  const itemLevelPriceSuggestion = itemLevelPriceSuggestionDTO?.map(PriceSuggestionPrice.fromDto);

  const updatePriceSuggestionPrioritization = useCallback(
    (value: PriceSuggestionType[]) => {
      priceSuggestionController.field.onChange(value);
    },
    [priceSuggestionController.field]
  );

  const getPriceTypeValue = useCallback(
    (priceSuggestionType: PriceSuggestionType) => {
      const priceSuggestion = itemLevelPriceSuggestion.find(
        (priceSuggestion) => priceSuggestion.priceSuggestionType === priceSuggestionType
      );
      return t('priceSuggestion.pricePerUnit', {
        price: priceSuggestion?.price,
        unit: priceSuggestion?.unit.description,
      });
    },
    [itemLevelPriceSuggestion, t]
  );

  return (
    <form>
      <Stack gap={3} pb={1}>
        {itemLevelPriceSuggestion.length === 0 ? (
          <Typography px={3}>{t('priceSuggestion.noPriceSuggestion')}</Typography>
        ) : (
          <>
            <FormControl error={!!priceSuggestionController.fieldState.error} required>
              <PriceSuggestionPrioritization
                priceSuggestionPriority={priceSuggestionController.field.value}
                availablePriceSuggestions={itemLevelPriceSuggestion.map(
                  (type) => type.priceSuggestionType
                )}
                updatePriceSuggestionPrioritazion={updatePriceSuggestionPrioritization}
                getPriceTypeValue={getPriceTypeValue}
              />

              {priceSuggestionController.fieldState.error && (
                <Stack px={3}>
                  <HelperText id={'pricesuggestion'} status={'error'}>
                    {t('priceSuggestion.error.selectPriceSuggestion')}
                  </HelperText>
                </Stack>
              )}
            </FormControl>
            <Stack px={3}>
              <ConditionDatesForm excludeToDate labelAlignment="column" />
            </Stack>
          </>
        )}
      </Stack>
    </form>
  );
}
